import { Connection, PublicKey } from '@solana/web3.js';
import { CryptoAsset } from '../../types/crypto';

// Use Jupiter API for token metadata and prices
let jupiterTokensCache: any = null;

const fetchJupiterTokens = async () => {
  if (jupiterTokensCache) return jupiterTokensCache;
  try {
    const res = await fetch('https://tokens.jup.ag/tokens?tags=verified');
    const tokens = await res.json();
    const tokenMap = new Map();
    tokens.forEach((t: any) => {
      tokenMap.set(t.address, t);
    });
    jupiterTokensCache = tokenMap;
    return tokenMap;
  } catch (e) {
    console.error('Failed to fetch Jupiter tokens', e);
    return new Map();
  }
};

const fetchJupiterPrices = async (mints: string[]) => {
  if (mints.length === 0) return {};
  try {
    const ids = mints.join(',');
    const res = await fetch(`https://price.jup.ag/v4/price?ids=${ids}`);
    const data = await res.json();
    return data.data || {};
  } catch (e) {
    console.error('Failed to fetch Jupiter prices', e);
    return {};
  }
};

export const fetchSolanaData = async (address: string) => {
  try {
    // We can use a public mainnet endpoint that supports CORS
    const connection = new Connection('https://solana-rpc.publicnode.com');
    const pubKey = new PublicKey(address);

    // 1. Fetch Native SOL Balance
    const lamports = await connection.getBalance(pubKey);
    const nativeBalance = lamports / 1e9;

    let nativePriceUsd = 0;
    const bRes = await fetch('https://api.binance.com/api/v3/ticker/price?symbol=SOLUSDT');
    if (bRes.ok) {
      const bData = await bRes.json();
      nativePriceUsd = parseFloat(bData.price);
    }
    
    // If Coingecko fails, fallback to Jupiter for SOL price
    if (!nativePriceUsd) {
        const jupSol = await fetchJupiterPrices(['So11111111111111111111111111111111111111112']);
        if (jupSol['So11111111111111111111111111111111111111112']) {
            nativePriceUsd = jupSol['So11111111111111111111111111111111111111112'].price;
        }
    }

    const nativeUsdValue = nativeBalance * nativePriceUsd;

    // Fetch specifically for popular tokens like USDC since querying all by programId is blocked
    const popularMints = [
      { mint: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v', symbol: 'USDC', name: 'USD Coin', icon: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v/logo.png' },
      { mint: 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB', symbol: 'USDT', name: 'USDT', icon: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB/logo.svg' }
    ];

    const assets: CryptoAsset[] = [];
    const mintsToPrice: string[] = [];
    
    for (const token of popularMints) {
      try {
        const res = await fetch('https://solana-rpc.publicnode.com', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0',
            id: 1,
            method: 'getTokenAccountsByOwner',
            params: [
              address,
              { mint: token.mint },
              { encoding: 'jsonParsed' }
            ]
          })
        });
        const data = await res.json();
        if (data.result && data.result.value && data.result.value.length > 0) {
          const accountData = data.result.value[0].account.data.parsed.info;
          const balance = accountData.tokenAmount.uiAmount;
          if (balance > 0) {
             mintsToPrice.push(token.mint);
             assets.push({
               id: token.mint,
               contractAddress: token.mint,
               name: token.name,
               symbol: token.symbol,
               balance: balance,
               usdValue: 0,
               priceUsd: 0,
               icon: token.icon
             });
          }
        }
      } catch (e) {
        console.warn(`Failed to fetch ${token.symbol} for Solana`, e);
      }
    }

    if (mintsToPrice.length > 0) {
      const prices = await fetchJupiterPrices(mintsToPrice);
      assets.forEach(asset => {
        if (asset.id && prices[asset.id]) {
          const price = prices[asset.id].price || 0;
          asset.priceUsd = price;
          asset.usdValue = asset.balance * price;
        }
      });
    }

    return {
      nativeBalance,
      nativeUsdValue,
      nativePriceUsd,
      assets
    };

  } catch (error) {
    console.error('Error fetching Solana data:', error);
    throw error;
  }
};
